import React, { useState, useMemo, useContext } from 'react';
import { Container, Row, Col, Form, Button, Spinner } from 'react-bootstrap';
import { fetchAuthSession } from 'aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';
import Alert from '../alert/alert';
import {
  cognitoVerifyTOTPSetup,
  changeAuthState,
  AuthState,
} from '../../helpr/authHelper';
import { IdentityContext } from '../../context/identity-context';
import styles from './custom-totp-setup.module.scss';

const CustomTOTPSetup = () => {
  // initialUser is the signIn output stored in identity context.
  // It contains nextStep.totpSetupDetails.sharedSecret — provided by Cognito
  // directly in the signIn response, so no extra API call is needed.
  const { initialUser, pendingUsername } = useContext(IdentityContext);

  const [code, setCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [alert, setAlert] = useState({
    msg: '',
    type: 'alert-danger-custom',
    isVisible: false,
    onClose: () => setAlert(prev => ({ ...prev, isVisible: false })),
  });

  // Read secret and build QR URI synchronously from what Cognito already gave us
  const { sharedSecret, qrUri } = useMemo(() => {
    const secret = initialUser?.nextStep?.totpSetupDetails?.sharedSecret || '';
    if (!secret) return { sharedSecret: '', qrUri: '' };

    const issuer = encodeURIComponent('e-SIP Portal');
    const label = encodeURIComponent(pendingUsername ? `e-SIP Portal:${pendingUsername}` : 'e-SIP Portal');
    const uri = `otpauth://totp/${label}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
    return { sharedSecret: secret, qrUri: uri };
  }, [initialUser, pendingUsername]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setAlert(prev => ({
        ...prev,
        isVisible: true,
        msg: 'Please enter the 6-digit code from your authenticator app.',
      }));
      return;
    }

    setVerifyLoading(true);
    try {
      // cognitoVerifyTOTPSetup calls confirmSignIn({ challengeResponse: totpCode })
      // which completes the CONTINUE_SIGN_IN_WITH_TOTP_SETUP challenge and issues tokens
      const output = await cognitoVerifyTOTPSetup(code);

      if (output?.isSignedIn) {
        const session = await fetchAuthSession();
        changeAuthState(AuthState.SignedIn, session);
      } else {
        // Unexpected state — send back to login
        changeAuthState(AuthState.SignIn);
      }
    } catch (err) {
      console.error('TOTP verify error:', err);
      setAlert(prev => ({
        ...prev,
        isVisible: true,
        msg: 'Invalid code. Please enter the current 6-digit code shown in your authenticator app.',
      }));
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleBackToLogin = () => {
    changeAuthState(AuthState.SignIn);
  };

  return (
    <div className={styles['custom-container']}>
      <Container>
        <Row>
          <Col lg={{ span: 6, offset: 3 }} md={{ span: 8, offset: 2 }}>
            <div className={styles['setup-wrapper']}>
              <div className={styles['app-title-section']}>
                <span>{process.env.REACT_APP_TITLE}</span>
              </div>

              <div className={styles['setup-title-section']}>
                <span>Set Up Two-Factor Authentication</span>
              </div>

              <div className={styles['setup-description']}>
                <p>
                  Your account requires two-factor authentication. Scan the QR code below
                  using an authenticator app such as <strong>Google Authenticator</strong> or{' '}
                  <strong>Microsoft Authenticator</strong>, then enter the 6-digit code to activate.
                </p>
              </div>

              <Alert className={'h-100'} options={alert} />

              {!sharedSecret ? (
                <div className={styles['loading-section']}>
                  <span className={styles['loading-text']}>
                    Setup data unavailable. Please go back and sign in again.
                  </span>
                </div>
              ) : (
                <>
                  <div className={styles['qr-section']}>
                    <div className={styles['qr-wrapper']}>
                      <QRCodeSVG value={qrUri} size={200} level="M" />
                    </div>

                    <div className={styles['manual-key-section']}>
                      <button
                        type="button"
                        className={styles['toggle-secret-btn']}
                        onClick={() => setShowSecret(v => !v)}
                      >
                        {showSecret ? 'Hide' : "Can't scan? Show manual entry key"}
                      </button>
                      {showSecret && (
                        <div className={styles['secret-key']}>
                          <span className={styles['secret-label']}>Manual entry key:</span>
                          <code className={styles['secret-value']}>{sharedSecret}</code>
                        </div>
                      )}
                    </div>
                  </div>

                  <Form onSubmit={handleVerify} className={styles['form-section']}>
                    <Form.Group className={styles['input-section']} controlId="totpCode">
                      <Form.Control
                        className={styles['totp-input']}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="Enter 6-digit code from app"
                        value={code}
                        onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                      />
                    </Form.Group>

                    <div className={styles['action-section']}>
                      <Button
                        type="submit"
                        className={styles['verify-button']}
                        disabled={verifyLoading || code.length !== 6}
                      >
                        {verifyLoading ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          'VERIFY & ACTIVATE'
                        )}
                      </Button>
                    </div>
                  </Form>
                </>
              )}

              <div className={styles['back-section']}>
                <button
                  type="button"
                  className={styles['back-link']}
                  onClick={handleBackToLogin}
                >
                  Back to Login
                </button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CustomTOTPSetup;
